import React, { useState } from 'react';
import type {
  Candidate,
  CandidateRedux,
  Certification,
} from '@axiom/validation';
import { sortBy } from 'lodash';
import {
  CertificationPlaceholderIcon,
  Gutter,
  CondensedHeader,
  IconButton,
  ContextMenu,
  ContextMenuItem,
  Layout,
  CondensedMedium,
} from '@axiom/ui';

import NoContentPlaceholder from '../NoContentPlaceholder/NoContentPlaceholder';
import { CertificationForm } from '../CertificationForm/CertificationForm';
import { CertificationsItem } from '../CertificationsItem/CertificationsItem';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateCertificationsApi } from '../../api/candidate-certifications-api';

const MAX_ENTRIES = 10;

export interface CertificationsProps {
  candidate: CandidateRedux | Candidate;
  name: string;
}

export const CertificationsList = ({
  candidate,
  name,
}: CertificationsProps) => {
  const [certificationType, setCertificationType] = useState<
    'Qualification' | 'Certifications & Awards' | null
  >(null);
  const [selectedCertification, setSelectedCertification] =
    useState<Certification | null>(null);
  const sortedCertifications = sortBy(
    Array.isArray(candidate.certifications) ? candidate.certifications : [],
    ['institution']
  );
  const qualifications = new Set([
    'State Exam',
    'Qualification',
    'Year of Call',
  ]);
  return (
    <Gutter name={name} vertical="16px">
      <Layout position="space-between middle">
        <CondensedHeader>
          Qualifications, Certifications, & Awards
        </CondensedHeader>
        <ContextMenu
          direction="left"
          anchor={
            <IconButton
              icon="plus"
              pattern="secondary"
              variation="minimal"
              name="ADDCERTIFICATIONBUTTON"
            />
          }
        >
          <ContextMenuItem
            name="ADD_QUALIFICATION_MENU_ITEM"
            disabled={sortedCertifications.length >= MAX_ENTRIES}
            onClick={() => {
              setCertificationType('Qualification');
            }}
          >
            Add Qualification
          </ContextMenuItem>
          <ContextMenuItem
            disabled={sortedCertifications.length >= MAX_ENTRIES}
            name="ADD_CERTIFICATION_AWARDS_MENU_ITEM"
            onClick={() => {
              setCertificationType('Certifications & Awards');
            }}
          >
            Add Certifications & Awards
          </ContextMenuItem>
        </ContextMenu>
      </Layout>
      {sortedCertifications.length > 0 ? (
        <>
          {sortedCertifications.map(certification => (
            <CertificationsItem
              key={certification.id}
              name="CERTIFICATION_ITEM"
              certification={certification}
              onEdit={() => {
                setSelectedCertification(certification);
                setCertificationType(
                  qualifications.has(certification.award)
                    ? 'Qualification'
                    : 'Certifications & Awards'
                );
              }}
              onDelete={async () => {
                await CandidateCertificationsApi.deleteCertification(
                  certification.id
                );
                await CandidateApi.refreshCandidate({
                  candidateId: candidate.id,
                });
              }}
            />
          ))}
          <CondensedMedium name="CERTIFICATIONS_FOOTNOTE">
            ({MAX_ENTRIES} entries maximum)
          </CondensedMedium>
        </>
      ) : (
        <NoContentPlaceholder
          image={<CertificationPlaceholderIcon />}
          title="No Certifications & Awards"
          text="Add your certifications and awards."
        />
      )}
      {certificationType ? (
        <CertificationForm
          name="CERTIFICATION_FORM"
          candidate={candidate}
          refreshData={async () => {
            await CandidateApi.refreshCandidate({ candidateId: candidate.id });
            setCertificationType(null);
          }}
          certification={selectedCertification}
          certificationType={certificationType}
          onClose={() => {
            setCertificationType(null);
            setSelectedCertification(null);
          }}
        />
      ) : null}
    </Gutter>
  );
};
